import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut, Lock } from "lucide-react";
import { Logo } from "../components/site/Logo";
import { refreshSpecials } from "../lib/hours";
import { Inbox } from "./AdminInbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
http.interceptors.response.use(undefined, async (err) => {
  const cfg = err.config;
  if (err.response?.status === 401 && !cfg._retry && !cfg.url.includes("/auth/")) {
    cfg._retry = true;
    await http.post("/auth/refresh");
    return http(cfg);
  }
  return Promise.reject(err);
});

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Något gick fel";
};

const Login = ({ onDone }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/login", { username: u, password: p });
      onDone();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="admin-login-form" onSubmit={submit} className="mx-auto mt-16 max-w-sm rounded-[24px] bg-white p-8 shadow-xl ring-1 ring-[#721121]/10">
      <Lock className="text-[#721121]" />
      <h1 className="font-display mt-4 text-3xl">Admin – öppettider</h1>
      <label className="mt-6 block text-xs uppercase tracking-[0.18em] text-[#57534E]">Användarnamn
        <input data-testid="admin-username-input" className="lb-input mt-1.5" value={u} onChange={(e) => setU(e.target.value)} autoComplete="username" />
      </label>
      <label className="mt-4 block text-xs uppercase tracking-[0.18em] text-[#57534E]">Lösenord
        <input data-testid="admin-password-input" type="password" className="lb-input mt-1.5" value={p} onChange={(e) => setP(e.target.value)} autoComplete="current-password" />
      </label>
      <button data-testid="admin-login-button" disabled={busy} className="btn-wine mt-6 w-full justify-center">{busy ? "Loggar in…" : "Logga in"}</button>
    </form>
  );
};

const EMPTY = { date: "", closed: true, open: "10:00", close: "16:00", note: "" };

const Manager = ({ onLogout }) => {
  const [list, setList] = useState([]);
  const [v, setV] = useState(EMPTY);
  const load = async () => {
    const r = await http.get("/hours/specials");
    setList(Array.isArray(r.data) ? r.data : []);
    refreshSpecials();
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!v.date) return toast.error("Välj ett datum");
    try {
      await http.post("/admin/specials", { date: v.date, closed: v.closed, open: v.closed ? null : v.open, close: v.closed ? null : v.close, note: v.note || null });
      toast.success("Sparat");
      setV(EMPTY);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };
  const remove = async (id) => {
    try {
      await http.delete(`/admin/specials/${id}`);
      toast.success("Borttaget");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div className="mx-auto mt-10 max-w-2xl space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Avvikande öppettider</h1>
        <button data-testid="admin-logout-button" onClick={onLogout} className="flex items-center gap-2 text-sm text-[#721121]"><LogOut size={16} /> Logga ut</button>
      </div>
      <p className="text-sm text-[#57534E]">Ordinarie tider: måndag–lördag 10:00–21:00, söndag stängt. Lägg till helgdagar eller andra datum med andra tider.</p>

      <form data-testid="special-hours-form" onSubmit={add} className="grid grid-cols-2 gap-4 rounded-[24px] bg-white p-6 ring-1 ring-[#721121]/10">
        <label className="col-span-2 block text-xs uppercase tracking-[0.18em] text-[#57534E] sm:col-span-1">Datum
          <input data-testid="special-date-input" type="date" className="lb-input mt-1.5" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} />
        </label>
        <label className="col-span-2 block text-xs uppercase tracking-[0.18em] text-[#57534E] sm:col-span-1">Notering (valfri)
          <input data-testid="special-note-input" className="lb-input mt-1.5" placeholder="t.ex. Julafton" value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} />
        </label>
        <div className="col-span-2 flex gap-2">
          {[[true, "Stängt"], [false, "Särskilda tider"]].map(([c, l]) => (
            <button type="button" key={l} data-testid={`special-mode-${c ? "closed" : "hours"}`} onClick={() => setV({ ...v, closed: c })} className={`flex-1 rounded-full py-2.5 text-sm transition-colors duration-300 ${v.closed === c ? "bg-[#721121] text-white" : "bg-[#FAF7F2] text-[#1C1917] ring-1 ring-[#721121]/15"}`}>{l}</button>
          ))}
        </div>
        {!v.closed && (
          <>
            <label className="block text-xs uppercase tracking-[0.18em] text-[#57534E]">Öppnar
              <input data-testid="special-open-input" type="time" className="lb-input mt-1.5" value={v.open} onChange={(e) => setV({ ...v, open: e.target.value })} />
            </label>
            <label className="block text-xs uppercase tracking-[0.18em] text-[#57534E]">Stänger
              <input data-testid="special-close-input" type="time" className="lb-input mt-1.5" value={v.close} onChange={(e) => setV({ ...v, close: e.target.value })} />
            </label>
          </>
        )}
        <button data-testid="special-save-button" className="btn-wine col-span-2 justify-center">Spara</button>
      </form>

      <ul data-testid="special-hours-list" className="divide-y divide-[#721121]/10 rounded-[24px] bg-white ring-1 ring-[#721121]/10">
        {list.length === 0 && <li className="p-6 text-sm text-[#57534E]">Inga kommande avvikande öppettider.</li>}
        {list.map((s) => (
          <li key={s.id} data-testid={`special-row-${s.date}`} className="flex items-center justify-between gap-4 p-5">
            <div>
              <p className="font-medium">{new Date(`${s.date}T12:00:00Z`).toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}</p>
              <p className="text-sm text-[#57534E]">{s.closed ? "Stängt" : `${s.open}–${s.close}`}{s.note ? ` · ${s.note}` : ""}</p>
            </div>
            <button data-testid={`special-delete-${s.date}`} onClick={() => remove(s.id)} aria-label="Ta bort" className="grid h-10 w-10 place-items-center rounded-full text-[#721121] ring-1 ring-[#721121]/20 hover:bg-[#721121] hover:text-white"><Trash2 size={16} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function AdminPage() {
  const [auth, setAuth] = useState(null);
  const [tab, setTab] = useState("inbox");
  const [openCount, setOpenCount] = useState(0);
  useEffect(() => {
    http.get("/auth/me").then(() => setAuth(true)).catch(async () => {
      try {
        await http.post("/auth/refresh");
        setAuth(true);
      } catch {
        setAuth(false);
      }
    });
  }, []);
  const logout = async () => {
    await http.post("/auth/logout");
    setAuth(false);
  };
  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FDFBF7] px-5 pb-20 pt-8 text-[#1C1917]">
      <div className="mx-auto max-w-2xl"><a href="/" data-testid="admin-home-link"><Logo /></a></div>
      {auth === null && <p className="mt-16 text-center text-sm text-[#57534E]">Laddar…</p>}
      {auth === false && <Login onDone={() => setAuth(true)} />}
      {auth === true && (
        <>
          <div className="mx-auto mt-8 flex max-w-2xl gap-2">
            {[["inbox", `Förfrågningar${openCount ? ` (${openCount})` : ""}`], ["hours", "Öppettider"]].map(([k, l]) => (
              <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 rounded-full py-3 text-sm font-medium transition-colors duration-300 ${tab === k ? "bg-[#1a0508] text-white" : "bg-white text-[#1C1917] ring-1 ring-[#721121]/15"}`}>{l}</button>
            ))}
          </div>
          {tab === "inbox" ? <Inbox http={http} onCount={setOpenCount} /> : <Manager onLogout={logout} />}
          {tab === "inbox" && <div className="mx-auto mt-8 max-w-2xl text-right"><button data-testid="admin-logout-inbox" onClick={logout} className="text-sm text-[#721121]">Logga ut</button></div>}
        </>
      )}
    </div>
  );
}
